"use client";

import { useSession } from "@saas/auth/client";
import { useActiveOrganization } from "@saas/organizations/hooks/use-active-organization";
import { orpc } from "@shared/lib/orpc";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import {
	NAV_GROUPS,
	type NavEntry,
	orgHref,
	PARTNER_ICON,
	PLATFORM_ICON,
	SETTINGS_ENTRY,
} from "./nav";

export interface ResolvedEntry extends Omit<NavEntry, "segment"> {
	href: string;
	group?: string;
}

/**
 * The retailer the shell is showing and its navigation. The organization list
 * comes from the _saas route context (server-loaded), so the sidebar never
 * waits on a client query.
 */
export function useWorkspace(pathname?: string) {
	const { user, session } = useSession();
	const { organizations } = useRouteContext({ from: "/_saas" });
	const { activeOrganization, activeOrganizationUserRole } =
		useActiveOrganization();

	const organization =
		activeOrganization ??
		organizations?.find(
			(org) => org.id === session?.activeOrganizationId,
		) ??
		organizations?.[0] ??
		null;

	const resolve = (entry: NavEntry, group?: string): ResolvedEntry | null =>
		organization
			? {
					...entry,
					group,
					href: orgHref(organization.slug, entry.segment),
				}
			: null;

	const groups = NAV_GROUPS.map((group) => ({
		label: group.label,
		entries: group.entries
			.map((entry) => resolve(entry, group.label))
			.filter((entry): entry is ResolvedEntry => entry !== null),
	})).filter((group) => group.entries.length > 0);

	const settings = resolve(SETTINGS_ENTRY);
	const platform: ResolvedEntry | null =
		user?.role === "admin"
			? {
					id: "platform",
					label: "Platform",
					icon: PLATFORM_ICON,
					href: "/app/platform",
					hint: "Every retailer, usage and cost",
					keywords: ["admin", "retailers", "staff"],
				}
			: null;
	// Partner staff get their console. Memberships change rarely: read once per session.
	const memberships = useQuery({
		...orpc.partner.memberships.queryOptions(),
		enabled: Boolean(user),
		staleTime: Number.POSITIVE_INFINITY,
	});
	// On the console itself the entry shows at once (the list loads after hydration).
	const partner: ResolvedEntry | null =
		memberships.data?.partners.length ||
		pathname?.startsWith("/app/partner")
			? {
					id: "partner",
					label: "Partner",
					icon: PARTNER_ICON,
					href: "/app/partner",
					hint: "Your retailers, partner keys and webhooks",
					keywords: ["reseller", "retailers", "provisioning"],
				}
			: null;
	const footer = [settings, partner, platform].filter(
		(entry): entry is ResolvedEntry => entry !== null,
	);
	const all = [...groups.flatMap((group) => group.entries), ...footer];

	// Longest matching href wins so /settings/members highlights Settings.
	// Account settings share the Settings area, so they highlight it too.
	const current = pathname?.startsWith("/app/settings")
		? (settings ?? undefined)
		: pathname
			? all
					.filter((entry) =>
						entry.id === "overview"
							? pathname === entry.href ||
								pathname === `${entry.href}/`
							: pathname === entry.href ||
								pathname.startsWith(`${entry.href}/`),
					)
					.sort((a, b) => b.href.length - a.href.length)[0]
			: undefined;

	return {
		user,
		organization,
		organizations: organizations ?? [],
		role: activeOrganizationUserRole,
		groups,
		footer,
		all,
		current,
	};
}
